/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState } from 'react';
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle,
  Button,
  Select,
  Badge
} from '@etherisc/ui-kit';
import { 
  TrendingUp, 
  TrendingDown, 
  DollarSign, 
  Users, 
  AlertTriangle, 
  CheckCircle,
  BarChart3,
  PieChart,
  RefreshCw
} from 'lucide-react';
import { 
  useMultiClientBalanceSummary,
  useClientReceivablesAging 
} from '../../hooks/insurance/useInsuranceReporting';
import { 
  useReconciliationReport,
  useCommissionSummary 
} from '../../hooks/insurance/useInsuranceBatch';
import { useLedgerReports } from '../../hooks/ledger/useLedgerReports';
import { formatDate, formatCurrency } from '../../utils/formatters';

export const FinancialAnalyticsDashboard = () => {
  const [selectedPeriod, setSelectedPeriod] = useState<string>('');
  const [asOfDate] = useState<Date>(new Date());

  const { periods } = useLedgerReports();
  const { data: balanceData, isLoading: balanceLoading } = useMultiClientBalanceSummary({ periodId: selectedPeriod });
  const { data: agingData, isLoading: agingLoading } = useClientReceivablesAging(asOfDate);
  const { data: reconciliationData, isLoading: reconciliationLoading } = useReconciliationReport({ periodId: selectedPeriod });
  const { data: commissionData, isLoading: commissionLoading } = useCommissionSummary({});

  // Calculate KPIs
  const kpis = {
    totalOutstanding: agingData?.reduce((sum, item) => sum + parseFloat(item.outstandingAmount), 0) || 0,
    currentReceivables: agingData?.filter(item => item.agingBucket === 'current')
      .reduce((sum, item) => sum + parseFloat(item.outstandingAmount), 0) || 0,
    overdueReceivables: agingData?.filter(item => item.agingBucket !== 'current')
      .reduce((sum, item) => sum + parseFloat(item.outstandingAmount), 0) || 0,
    reconciliationVariances: reconciliationData?.accounts?.filter(acc => parseFloat(acc.variance) !== 0).length || 0,
    totalVarianceAmount: reconciliationData?.accounts?.reduce((sum, acc) => sum + Math.abs(parseFloat(acc.variance)), 0) || 0,
    commissionOutstanding: commissionData?.summary ? parseFloat(commissionData.summary.totalOutstanding) : 0,
    activeClients: balanceData?.length || 0,
    activeAgents: commissionData?.summary?.agentCount || 0
  };

  const isLoading = balanceLoading || agingLoading || reconciliationLoading || commissionLoading;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <BarChart3 className="h-6 w-6" />
            Financial Analytics Dashboard
          </h2>
          <p className="text-muted-foreground">
            Real-time financial position analysis and key performance indicators
          </p>
        </div>
        <div className="flex gap-2">
          <Select
            label="Period"
            value={selectedPeriod || 'none'}
            onValueChange={(value) => setSelectedPeriod(value === 'none' ? '' : value)}
            options={[
              { value: 'none', label: 'Current Period' },
              ...(periods?.map((period: any) => ({
                value: period.id,
                label: `${period.name} (${formatDate(period.startDate)} - ${formatDate(period.endDate)})`
              })) || [])
            ]}
          />
          <Button
            variant="outline"
            size="sm"
            disabled={isLoading}
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Key Performance Indicators */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Outstanding</p>
                <p className="text-3xl font-bold text-blue-600">
                  {formatCurrency(kpis.totalOutstanding.toString(), 'EUR')}
                </p>
                <div className="flex items-center mt-2">
                  <span className="text-sm text-muted-foreground">
                    Current: {formatCurrency(kpis.currentReceivables.toString(), 'EUR')}
                  </span>
                </div>
              </div>
              <DollarSign className="h-12 w-12 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Overdue Receivables</p>
                <p className="text-3xl font-bold text-orange-600">
                  {formatCurrency(kpis.overdueReceivables.toString(), 'EUR')}
                </p>
                <div className="flex items-center mt-2">
                  {kpis.overdueReceivables > 0 ? (
                    <Badge variant="warning" className="flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3" />
                      Attention Required
                    </Badge>
                  ) : (
                    <Badge variant="success" className="flex items-center gap-1">
                      <CheckCircle className="h-3 w-3" />
                      All Current
                    </Badge>
                  )}
                </div>
              </div>
              <TrendingDown className="h-12 w-12 text-orange-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Reconciliation Status</p>
                <p className="text-3xl font-bold">
                  {kpis.reconciliationVariances === 0 ? (
                    <span className="text-green-600">Clean</span>
                  ) : (
                    <span className="text-red-600">{kpis.reconciliationVariances}</span>
                  )}
                </p>
                <div className="flex items-center mt-2">
                  {kpis.reconciliationVariances === 0 ? (
                    <Badge variant="success" className="flex items-center gap-1">
                      <CheckCircle className="h-3 w-3" />
                      Reconciled
                    </Badge>
                  ) : (
                    <Badge variant="destructive" className="flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3" />
                      {formatCurrency(kpis.totalVarianceAmount.toString(), 'EUR')} Variance
                    </Badge>
                  )}
                </div>
              </div>
              <BarChart3 className="h-12 w-12 text-purple-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Commission Outstanding</p>
                <p className="text-3xl font-bold text-green-600">
                  {formatCurrency(kpis.commissionOutstanding.toString(), 'EUR')}
                </p>
                <div className="flex items-center mt-2">
                  <span className="text-sm text-muted-foreground">
                    {kpis.activeAgents} Active Agents
                  </span>
                </div>
              </div>
              <TrendingUp className="h-12 w-12 text-green-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Financial Position Analysis */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PieChart className="h-5 w-5" />
              Receivables Aging Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent>
            {agingLoading ? (
              <div className="text-center py-8">Loading aging analysis...</div>
            ) : agingData && agingData.length > 0 ? (
              <div className="space-y-4">
                {['current', '30_days', '60_days', '90_plus_days'].map(bucket => {
                  const bucketData = agingData.filter(item => item.agingBucket === bucket);
                  const bucketAmount = bucketData.reduce((sum, item) => sum + parseFloat(item.outstandingAmount), 0);
                  const percentage = kpis.totalOutstanding > 0 ? (bucketAmount / kpis.totalOutstanding) * 100 : 0;
                  
                  const bucketLabel = bucket === 'current' ? 'Current' :
                                   bucket === '30_days' ? '30 Days' :
                                   bucket === '60_days' ? '60 Days' : '90+ Days';
                  
                  const bucketColor = bucket === 'current' ? 'bg-green-500' :
                                    bucket === '30_days' ? 'bg-yellow-500' :
                                    bucket === '60_days' ? 'bg-orange-500' : 'bg-red-500';

                  return (
                    <div key={bucket} className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-4 h-4 rounded ${bucketColor}`} />
                        <span className="font-medium">{bucketLabel}</span>
                      </div>
                      <div className="text-right">
                        <div className="font-semibold">
                          {formatCurrency(bucketAmount.toString(), 'EUR')}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {percentage.toFixed(1)}%
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                No aging data available
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Client Portfolio Health
            </CardTitle>
          </CardHeader>
          <CardContent>
            {balanceLoading ? (
              <div className="text-center py-8">Loading portfolio analysis...</div>
            ) : balanceData && balanceData.length > 0 ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <div className="text-2xl font-bold text-blue-600">{kpis.activeClients}</div>
                    <div className="text-sm text-muted-foreground">Active Clients</div>
                  </div>
                  <div className="text-center p-4 bg-green-50 rounded-lg">
                    <div className="text-2xl font-bold text-green-600">
                      {balanceData.filter((client: any) => parseFloat(client.outstandingBalance) === 0).length}
                    </div>
                    <div className="text-sm text-muted-foreground">Paid in Full</div>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <h4 className="font-medium">Top Outstanding Clients</h4>
                  {balanceData
                    .filter((client: any) => parseFloat(client.outstandingBalance) > 0)
                    .sort((a: any, b: any) => parseFloat(b.outstandingBalance) - parseFloat(a.outstandingBalance))
                    .slice(0, 5)
                    .map((client: any) => (
                      <div key={client.clientId} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                        <div>
                          <div className="font-medium text-sm">{client.clientName}</div>
                          <div className="text-xs text-muted-foreground">{client.clientCode}</div>
                        </div>
                        <div className="font-semibold text-orange-600">
                          {formatCurrency(client.outstandingBalance, client.currency)}
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                No client data available
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Reconciliation Health Check */}
      {selectedPeriod && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5" />
              Reconciliation Health Check
            </CardTitle>
          </CardHeader>
          <CardContent>
            {reconciliationLoading ? (
              <div className="text-center py-8">Loading reconciliation analysis...</div>
            ) : reconciliationData?.accounts ? (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="text-center p-4 bg-green-50 rounded-lg">
                    <div className="text-2xl font-bold text-green-600">
                      {reconciliationData.accounts.filter(acc => parseFloat(acc.variance) === 0).length}
                    </div>
                    <div className="text-sm text-muted-foreground">Reconciled Accounts</div>
                  </div>
                  <div className="text-center p-4 bg-red-50 rounded-lg">
                    <div className="text-2xl font-bold text-red-600">
                      {reconciliationData.accounts.filter(acc => parseFloat(acc.variance) !== 0).length}
                    </div>
                    <div className="text-sm text-muted-foreground">Accounts with Variances</div>
                  </div>
                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <div className="text-2xl font-bold text-blue-600">
                      {formatCurrency(kpis.totalVarianceAmount.toString(), 'EUR')}
                    </div>
                    <div className="text-sm text-muted-foreground">Total Variance</div>
                  </div>
                </div>

                {kpis.reconciliationVariances > 0 && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertTriangle className="h-5 w-5 text-yellow-600" />
                      <h4 className="font-medium text-yellow-800">Reconciliation Attention Required</h4>
                    </div>
                    <div className="space-y-2">
                      {reconciliationData.accounts
                        .filter(acc => parseFloat(acc.variance) !== 0)
                        .map(acc => (
                          <div key={acc.accountId} className="flex items-center justify-between text-sm">
                            <span className="font-medium">{acc.accountName} ({acc.accountCode})</span>
                            <span className="font-semibold text-red-600">
                              {formatCurrency(acc.variance, acc.currency)} variance
                            </span>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                Select a period to view reconciliation health
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Commission Performance */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Commission Performance
          </CardTitle>
        </CardHeader>
        <CardContent>
          {commissionLoading ? (
            <div className="text-center py-8">Loading commission analysis...</div>
          ) : commissionData?.summary ? (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <div className="text-2xl font-bold text-blue-600">
                  {formatCurrency(commissionData.summary.totalDue, 'EUR')}
                </div>
                <div className="text-sm text-muted-foreground">Total Due</div>
              </div>
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <div className="text-2xl font-bold text-green-600">
                  {formatCurrency(commissionData.summary.totalPaid, 'EUR')}
                </div>
                <div className="text-sm text-muted-foreground">Total Paid</div>
              </div>
              <div className="text-center p-4 bg-orange-50 rounded-lg">
                <div className="text-2xl font-bold text-orange-600">
                  {formatCurrency(commissionData.summary.totalOutstanding, 'EUR')}
                </div>
                <div className="text-sm text-muted-foreground">Outstanding</div>
              </div>
              <div className="text-center p-4 bg-purple-50 rounded-lg">
                <div className="text-2xl font-bold text-purple-600">{commissionData.summary.agentCount}</div>
                <div className="text-sm text-muted-foreground">Active Agents</div>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              No commission data available
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
